import * as PIXI from 'pixi.js';
import { Viewport } from 'pixi-viewport';
import { StoreApi } from 'zustand';
import gsap from 'gsap';

import { EntityRegistry } from '@/core/battle/EntityRegistry';
import { HighlightManager } from '@/core/battle/HighlightManager';
import { InputController } from '@/core/battle/InputController';
import { MovementExecutor } from '@/core/battle/MovementExecutor';
import { ControlStateMachine } from '@/core/battle/ControlStateMachine';
import { CombatExecutor } from '@/core/battle/CombatExecutor';
import { TurnManager } from '@/core/battle/TurnManager';
import { axialToPixel, coordsToId, getMapCenter, getSpawnPoints, pixelToAxial } from '@/core/utils/hex.utils';
import { generateTurnQueue } from '@/core/utils/battle.utils';
import { SkillManager } from '@/core/battle/SkillManager';
import ManagerRegistry from '@/core/battle/ManagerRegistry';
import { AIStrategicManager } from '@/core/battle/ai/strategic/AIStrategicManager';
import { AISystem } from '@/core/battle/ai/AISystem';
import { AITurnManager } from '@/core/battle/ai/AITurnManager';
import { TurnQueueRenderer } from '@/view/renderers/TurnQueueRenderer';
import { SkillBarRenderer } from '@/view/renderers/SkillBarRenderer';
import { DIFFICULTY, HEX_DIRECTIONS } from '@/core/constants/battle.constants';
import { CombatCalculator } from '@/core/battle/CombatCalculator';
import { useGlobalStore } from '@/store/useGlobalStore';
import { FactionSide, IViewportClickEvent, MapConfig } from '@/core/types/global.types';
import { CursorManager, CursorType } from '@/core/battle/CursorManager';
import { ExecutorManager } from '@/core/battle/executors/ExecutorManager';
import { AnimationController } from '@/core/battle/AnimationController';
import { BattleManager } from '@/core/battle/BattleManager';
import { HexMapGenerator } from '@/core/battle/HexMapGenerator';
import { TooltipManager } from '@/core/battle/TooltipManager';
import { UnitFactory } from '@/core/battle/UnitFactory';
import { Hex, TerrainType, FactionType, BattleStatus, ControlState } from '@/core/types/battle.types';
import { BattleBus } from '@/events/Events';
import { BattleStore, useBattleStore } from '@/store/useBattleStore';
import { BattleCameraManager } from '@/view/BattleCameraManager';
import { BattleHex } from '@/view/renderers/BattleHex';
import { DamageText } from '@/view/renderers/DamageText';
import { PathRenderer } from '@/view/renderers/PathRenderer';
import { UnitStack } from '@/view/renderers/UnitStack';
import { PointerState } from '@/core/utils/pointerState';
import { SettingsButton } from '@/view/renderers/SettingsButton';
import { resolveAsset } from '@/core/utils/common.utils';


export class BattleScene extends PIXI.Container {
    private viewport: Viewport;
    private mapLayer = new PIXI.Container();
    private highlightLayer = new PIXI.Container();
    private groundLayer = new PIXI.Container();
    private unitLayer = new PIXI.Container();
    private vfxLayer = new PIXI.Container();
    private cameraManager: BattleCameraManager;
    private cursorManager: CursorManager;
    private entityRegistry: EntityRegistry;
    private tooltipManager: TooltipManager;
    private pathRenderer: PathRenderer;
    private settingsButton!: SettingsButton;
    private turnQueueRenderer!: TurnQueueRenderer;
    private skillBarRenderer!: SkillBarRenderer;
    private animationController: AnimationController;
    private damageText: DamageText;
    private highlightManager: HighlightManager;
    private controlStateMachine: ControlStateMachine;
    private inputController: InputController;
    private movementExecutor: MovementExecutor;
    private combatExecutor: CombatExecutor;
    private skillManager: SkillManager;
    private aiStrategicManager: AIStrategicManager;
    private aiSystem: AISystem;
    private aiTurnManager: AITurnManager;
    private executorManager: ExecutorManager;

    private turnManager: TurnManager;
    private battleManager: BattleManager;
    
    private uiLayer = new PIXI.Container();
    private endText: PIXI.Text | null = null;
    private edgeTextureCache = new Map<number, PIXI.RenderTexture>();
    private statusUnsubscribe: (() => void) | null = null;

    private chunkSize = 8;
    private chunkSprites = new Map<string, PIXI.Sprite>();

    constructor(private app: PIXI.Application) {
        super();

        this.app.renderer.on('resize', () => {
            requestAnimationFrame(() => {
                this.cameraManager.updateSize(this.app.screen.width, this.app.screen.height);
                this.positionTurnQueue();
                this.positionSkillBar();
                this.positionSettingsBtn();
            });
        });

        this.viewport = new Viewport({
            screenWidth: this.app.screen.width,
            screenHeight: this.app.screen.height,
            worldWidth: 2000,
            worldHeight: 2000,
            events: this.app.renderer.events,
            ticker: app.ticker
        });

        this.addChild(this.viewport);
        this.addChild(this.uiLayer);
        this.viewport.addChild(this.mapLayer);
        this.viewport.addChild(this.highlightLayer);
        this.viewport.addChild(this.groundLayer);
        this.viewport.addChild(this.unitLayer);
        this.viewport.addChild(this.vfxLayer);
        this.unitLayer.sortableChildren = true;
        this.vfxLayer.sortableChildren = true;

        this.entityRegistry = new EntityRegistry();
        this.pathRenderer = new PathRenderer(this.groundLayer);
        
        this.animationController = new AnimationController();
        
        this.controlStateMachine = new ControlStateMachine();

        this.tooltipManager = new TooltipManager(this.controlStateMachine);
        this.turnManager = new TurnManager();

        this.highlightManager = new HighlightManager(this.entityRegistry, this.controlStateMachine);

        this.skillManager = new SkillManager(this.controlStateMachine, this.pathRenderer);

        this.movementExecutor = new MovementExecutor(
            this.animationController, 
            this.pathRenderer, 
            this.entityRegistry,
        );

        this.combatExecutor = new CombatExecutor(
            this.animationController, 
            this.entityRegistry,
            this.turnManager,
        );

        this.executorManager = new ExecutorManager(this.movementExecutor, this.combatExecutor, this.controlStateMachine,);
        
        this.inputController = new InputController(
            this.highlightManager,
            this.executorManager,
            this.entityRegistry,
            this.controlStateMachine,
            this.skillManager,
            this.turnManager,
        );

        // Strategic manager
        this.aiStrategicManager = new AIStrategicManager();
        
        // AI System (planning)
        this.aiSystem = AISystem.create(this.aiStrategicManager);
        
        // AI Turn Manager (execution)
        this.aiTurnManager = new AITurnManager(
            this.aiSystem,
            this.executorManager,
            this.turnManager,
            this.controlStateMachine
        );

        this.cameraManager = new BattleCameraManager(this.viewport);

        this.cursorManager = new CursorManager(this.app.canvas, this.controlStateMachine);
        this.cursorManager.set(CursorType.Default)
        
        this.battleManager = new BattleManager(
            this.pathRenderer, 
            this.inputController,
            this.skillManager,
            this.cameraManager,
            this.highlightManager,
            this.entityRegistry,
            this.controlStateMachine,
            this.turnManager,
            this.aiTurnManager,
        );

        // Register managers for React access
        ManagerRegistry.initialize(
            this.controlStateMachine,
            this.skillManager,
            this.turnManager,
            this.entityRegistry
        );
        
        
        this.damageText = new DamageText(
            (unitId) => this.entityRegistry.getUnit(unitId)
        );
        this.vfxLayer.addChild(this.damageText);
        
    }

    public async init() {
        console.log('[BattleScene] Init start');
        
        const { mapConfig } = useGlobalStore.getState();

        useBattleStore.getState().reset();
        console.log('[BattleScene] reset store');
        CombatCalculator.setConfig(DIFFICULTY.fast);

        await this.loadGameAssets();

        this.installBitmapFonts();
        await document.fonts.load('400 36px KyivType');
        await document.fonts.load('500 36px Alegreya');
        await document.fonts.load('700 36px Alegreya');
        await document.fonts.load('800 100px Alegreya');
        await document.fonts.ready;

        console.log('[BattleScene] game assets loaded');

        // Create map and units FIRST
        const mapBounds = this.initializeBattleMap(mapConfig);
        console.log('[BattleScene] Map initialized');

        // this.mapLayer.scale.y = 0.5;

        await this.initializeUnits(mapConfig);
        console.log('[BattleScene] Units initialized');
        
        this.cameraManager.initConstraints(mapBounds);
        
        // this.cameraManager.applyGridMask(this.mapLayer, mapBounds);
        
        this.cameraManager.updateSize(this.app.screen.width, this.app.screen.height);

        const unitPositions = this.getUnitPositions();
        
        await this.cameraManager.focusOnBattleCenter(unitPositions);
        this.statusUnsubscribe = useBattleStore.subscribe(
            state => state.status, 
            status => this.handleGameStatus(status)
        );

        this.settingsButton = new SettingsButton();

        this.settingsButton.onLayoutChange = () => {
            this.positionSettingsBtn();
        };

        this.positionSettingsBtn();

        this.turnQueueRenderer = new TurnQueueRenderer({
            portraitSize: 52,

            // colors: { ... } // Optional color overrides
        });

        this.turnQueueRenderer.onLayoutChange = () => {
            this.positionTurnQueue();
        };

        this.skillBarRenderer = new SkillBarRenderer(this.skillManager, this.turnManager, this.controlStateMachine, {
            direction: 'vertical', // 'vertical' якщо хочеш збоку
            gap: 8,
            iconSize: 54
        });

        this.skillBarRenderer.onLayoutChange = () => {
            this.positionSkillBar();
        };

        this.uiLayer.addChild(this.turnQueueRenderer);
        this.uiLayer.addChild(this.skillBarRenderer);
        this.uiLayer.addChild(this.settingsButton);
        
        
        this.initializeBattle();
        // this.positionTurnQueue();
        // this.positionSkillBar();
        

        this.inputController.initializeHotkeys();
        console.log('[BattleScene] Hotkeys initialized - scene ready');

        // this.viewport.on('pointermove', (event) => {
        //     const globalPos = event.data.global;
        //     BattleBus.emit('MOUSE_MOVE', { x: globalPos.x, y: globalPos.y });
        // });


        let rafPending = false;
        let lastHoveredHexId: string | null = null;

        // Виносимо очищення в окрему функцію
        const clearLastHover = () => {
            if (!lastHoveredHexId) return;
            
            const prev = this.entityRegistry.getHex(lastHoveredHexId);
            BattleBus.emit('HEX_OUT', { id: lastHoveredHexId });
            if (prev?.data.entityId) {
                BattleBus.emit('UNIT_OUT', { id: prev.data.entityId });
            }
            lastHoveredHexId = null;
        };

        this.viewport.on('pointermove', (event: PIXI.FederatedPointerEvent) => {
            if (rafPending) return;
            rafPending = true;
            const worldPos = this.viewport.toWorld(event.global);

            PointerState.copyFrom(worldPos);

            requestAnimationFrame(() => {
                rafPending = false;

                // Використовуємо локальні копії координат
                
                const hexCoords = pixelToAxial(worldPos.x, worldPos.y);
                
                // Якщо ми поза сіткою
                if (!hexCoords) {
                    clearLastHover();
                    return;
                }

                const hexId = coordsToId(hexCoords);
                if (hexId === lastHoveredHexId) return;

                // 3. Спочатку виходимо зі старого
                clearLastHover();

                // 4. Заходимо в новий
                const hex = this.entityRegistry.getHex(hexId);
                if (hex) {
                    lastHoveredHexId = hex.id;
                    BattleBus.emit('HEX_HOVER', { id: hex.id, position: PointerState });
                    
                    if (hex.data?.entityId) {
                        BattleBus.emit('UNIT_HOVER', { id: hex.data.entityId, position: PointerState });
                    }
                }
            });
        });

        this.viewport.on('pointerout', clearLastHover);

        this.viewport.on('clicked', (event: IViewportClickEvent) => {
            
            const hexCoords = pixelToAxial(event.world.x, event.world.y);
            if (!hexCoords) return;

            const hexId = coordsToId(hexCoords);
            
            const hex = this.entityRegistry.getHex(hexId);

            if (hex) {
                // Емітимо подію в шину
                BattleBus.emit('HEX_CLICK', { id: hex.id });

                console.log(
                    "hexid", hex.id, 
                    hex.data
                );
            }
        });

        BattleBus.on('FOCUS_ON_UNIT', this.handleFocusEvent);
    }

    private handleFocusEvent = async ({ unitId, force }: { unitId: string; force?: boolean }) => {
        const unit = useBattleStore.getState().units.get(unitId);
        if (unit) {
            const pixelCoords = axialToPixel(unit.q, unit.r);
            await this.cameraManager.focusOnPoint(pixelCoords, force, false);
        }
    };



    
    

    // private initializeBattleMap = () => {
    //     const mapGenerator = new HexMapGenerator({ size: 24, seed: "battle-of-kyi1872" });
    //     const mapData = mapGenerator.generate();

    //     useBattleStore.getState().setBattleMap(mapData);

    //     for (const [id, data] of mapData) {
    //         const hexView = new BattleHex(data);
    //         this.mapLayer.addChild(hexView);
    //         this.entityRegistry.registerHex(id, hexView);
    //     }
    // };

    private getChunkKey(q: number, r: number): string {
        const chunkQ = Math.floor(q / this.chunkSize);
        const chunkR = Math.floor(r / this.chunkSize);
        return `${chunkQ}_${chunkR}`;
    }

    private initializeBattleMap = (mapConfig: MapConfig) => {
        const mapGenerator = new HexMapGenerator(mapConfig);
        const mapData = mapGenerator.generate();
        useBattleStore.getState().setBattleMap(mapData);
console.log(Object.fromEntries(mapData));
        // 1. Створюємо всі об'єкти BattleHex, але поки не додаємо їх на сцену
        for (const [id, data] of mapData) {
            const hexView = new BattleHex(data);
            this.entityRegistry.registerHex(id, hexView);
            
            // Реєструємо інтерактивний шар (він завжди живий)
            this.highlightLayer.addChild(hexView);

            hexView?.getStaticVisuals();

            if (data.isObstacle) {
                const obstacle = hexView.getObstacleSprite();
                if (obstacle) {
                    this.unitLayer.addChild(obstacle);
                    obstacle.zIndex = obstacle.y;
                }
            }
        }

        // 2. Розраховуємо краї (edges) ДО запікання чанків
        // Тепер applyHillEdges просто додасть спрайти в edgeContainer всередині hexView
        this.applyHillEdges(mapData);

        // 3. Групуємо і запікаємо в чанки
        const chunkContainers = new Map<string, PIXI.Container>();
        
        for (const [id, data] of mapData) {
            const hexView = this.entityRegistry.getHex(id);
            if (!hexView) continue;

            const chunkKey = this.getChunkKey(data.q, data.r);
            if (!chunkContainers.has(chunkKey)) {
                const container = new PIXI.Container();
                container.eventMode = 'none';
                chunkContainers.set(chunkKey, container);
            }

            // Беремо статичну графіку (вона вже містить краї після applyHillEdges)
            const visuals = hexView.getStaticVisuals();
            chunkContainers.get(chunkKey)!.addChild(visuals);
        }

        // 4. Фінальне запікання кожного чанку в одну текстуру
        chunkContainers.forEach((container, key) => {
            container.children.sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));

            const bounds = container.getLocalBounds();
            const renderTexture = PIXI.RenderTexture.create({
                width: Math.ceil(bounds.width),
                height: Math.ceil(bounds.height),
                resolution: 1,
            });

            const matrix = new PIXI.Matrix();
            matrix.translate(-bounds.x, -bounds.y);

            this.app.renderer.render({
                container: container,
                target: renderTexture,
                transform: matrix
            });

            const chunkSprite = new PIXI.Sprite(renderTexture);
            chunkSprite.x = bounds.x;
            chunkSprite.y = bounds.y;
            
            this.chunkSprites.set(key, chunkSprite);
            this.mapLayer.addChild(chunkSprite);

            // Знищуємо контейнер чанку та його дітей (спрайти гексів), 
            // бо вони вже в текстурі.
            container.destroy({ children: true });
        });

        // КРОК 5: ОЧИЩЕННЯ (ВИНОСИМО ЗА МЕЖІ FOREACH)
        // Тепер, коли ВСІ чанки готові, ми чистимо посилання в гексах
        for (const [id, data] of mapData) {
            const hexView = this.entityRegistry.getHex(id);
            // Тут краще не робити .destroy(), а просто занулити посилання,
            // бо Pixi вже знищив об'єкти через container.destroy({ children: true })
            hexView?.clearStaticReferences(); 
        }
        
        this.mapLayer.eventMode = 'none';
        return this.mapLayer.getLocalBounds().rectangle;
    };



    private getEdgeBitmask(hex: Hex, mapData: Map<string, Hex>): number {
        let mask = 0;

        // Використовуємо forEach або map по самому масиву напрямків, 
        // тоді TS автоматично знатиме типи dq, dr та індекс dir
        HEX_DIRECTIONS.forEach((dirCoords, dirIndex) => {
            const { q: dq, r: dr } = dirCoords;
            
            const neighborId = coordsToId({ 
                q: hex.q + dq, 
                r: hex.r + dr 
            });

            const neighbor = mapData.get(neighborId);

            if (!neighbor || neighbor.terrain !== TerrainType.Hill) {
                mask |= (1 << dirIndex);
            }
        });

        return mask;
    }

    private applyHillEdges(mapData: Map<string, Hex>): Set<string> {
        const hillMap: Set<string> = new Set();

        for (const [id, hex] of mapData) {
            // Працюємо тільки з пагорбами
            if (hex.terrain !== TerrainType.Hill) continue;

            const hexView = this.entityRegistry.getHex(id);
            if (!hexView) continue;

            // Ітеруємося по всіх 6 напрямках напряму
            HEX_DIRECTIONS.forEach((dirCoords, dirIndex) => {
                const { q: dq, r: dr } = dirCoords;

                // Знаходимо сусіда
                const neighborId = coordsToId({ 
                    q: hex.q + dq, 
                    r: hex.r + dr 
                });
                const neighbor = mapData.get(neighborId);

                // Якщо сусіда немає або це не пагорб — додаємо візуальний край (edge)
                if (!neighbor || neighbor.terrain !== TerrainType.Hill) {
                    hexView.addEdgeSprite(dirIndex);
                    hillMap.add(hexView.id);
                }
            });
        }
        
        return hillMap;
    }

    private bakeEdgesUnique(renderer: PIXI.Renderer, mapData: Map<string, Hex>): void {
        for (const [id, hex] of mapData) {
            if (hex.terrain !== TerrainType.Hill) continue;

            const hexView = this.entityRegistry.getHex(id);
            if (!hexView) continue;

            const bitmask = this.getEdgeBitmask(hex, mapData);

            if (this.edgeTextureCache.has(bitmask)) {
                hexView.applyBakedEdges(this.edgeTextureCache.get(bitmask)!);
                continue;
            }

            const texture = hexView.bakeEdges(renderer);
            if (texture) {
                this.edgeTextureCache.set(bitmask, texture);
            }
        }
    }

    private async initializeUnits(mapConfig: MapConfig) {
        const { playerSide } = useGlobalStore.getState();

        const mapCenter = getMapCenter(mapConfig.size);
        const spawnPoints = getSpawnPoints(mapConfig.size, mapCenter);

        const ruthenianDefs: [string, number, number, number, FactionType][] = [
            ['druzhynnyk',   6,  3, 10, FactionType.Player],
            // ['striletz',  5,  4, 10, FactionType.Player],
            ['striletz',  5,  4, 10, FactionType.Player],
            ['varangian', 6,  4, 10, FactionType.Player],
            ['varangian', 6,  4, 10, FactionType.Player],
            ['gryd',      6,  5, 10, FactionType.Player],

            ['batyr',       17, -1, 10, FactionType.Enemy],
            ['batyr',       18, -2, 10, FactionType.Enemy],
            ['batyr',     17, -2, 10, FactionType.Enemy],
            ['batyr',     17, -3, 10, FactionType.Enemy],
            ['batyr',     18, -3, 10, FactionType.Enemy],
        ];

        const hordeDefs: [string, number, number, number, FactionType][] = [
            ['kipchak',       6,  3, 20, FactionType.Player],
            ['kipchak',5,  4, 20, FactionType.Player],
            ['kipchak',     6,  4, 10, FactionType.Player],
            ['kipchak',     6,  5, 10, FactionType.Player],
            ['kipchak',     6,  5, 10, FactionType.Player],

            ['druzhynnyk',  17, -1, 10, FactionType.Enemy],
            ['striletz', 18, -2, 10, FactionType.Enemy],
            ['striletz', 18, -2, 10, FactionType.Enemy],
            ['varangian',17, -2, 10, FactionType.Enemy],
            ['varangian',17, -2, 10, FactionType.Enemy],
            ['gryd',     17, -3, 10, FactionType.Enemy],
        ];

        const defs = playerSide === FactionSide.RUTHENIA ? mapConfig.playerUnits : mapConfig.enemyUnits;
        const attks = playerSide === FactionSide.RUTHENIA ? mapConfig.enemyUnits : mapConfig.playerUnits;

        const units: UnitStack[] = [];

        for (let i = 0; i < defs.length; i++) {
            const entry = defs[i];

            if (!entry) continue;
            
            units.push(await UnitFactory.createUnit(
                entry.id, 
                entry.unitId, 
                spawnPoints.player, 
                entry.count, 
                FactionType.Player
            ));
        }

        for (let i = 0; i < attks.length; i++) {
            const entry = attks[i];

            if (!entry) continue;
            
            units.push(await UnitFactory.createUnit(
                entry.id, 
                entry.unitId, 
                spawnPoints.enemy, 
                entry.count, 
                FactionType.Enemy
            ));
        }

        units.forEach((unit: UnitStack) => {
            this.unitLayer.addChild(unit);
            this.entityRegistry.registerUnit(unit.unitId, unit);
            useBattleStore.getState().updateHex(coordsToId(unit), { entityId: unit.unitId });
        });

        this.unitLayer.sortChildren();
    }

    private installBitmapFonts() {
        PIXI.BitmapFont.install({
            name: 'KyivTypeBitmap',
            style: new PIXI.TextStyle({
                fontFamily: 'KyivType',
                fontSize: 36,
                fontWeight: '700',
                fill: 0xe0d7c6,
                stroke: { color: 0x1a1714, width: 4 },
            }),
            resolution: 2,
        });
    }


    private initializeBattle() {
        const state = useBattleStore.getState();
        const unitsArray = Array.from(state.units.values());
        const queue = generateTurnQueue(unitsArray);
        const firstPool = queue[0];
        if (!firstPool) return;
        state.setTurnQueue(queue);
        state.setStatus(BattleStatus.Playing);
        
        state.setActiveFaction(firstPool.faction);
        
        state.setSelectedUnit(firstPool.unitIds[0] || null);

    }

    private getUnitPositions = (): {x: number, y: number}[] => {
        const units = Array.from(useBattleStore.getState().units.values());

        const unitPositions = units.map(u => {
            const unit = this.entityRegistry.getUnit(u.unitId);
            return { x: unit?.x || 0, y: unit?.y || 0 };
        });
        return unitPositions;
    };

    private positionSettingsBtn() {
        const paddingH = 20;
        if (this.settingsButton) {
            this.settingsButton.position.set(
                this.app.screen.width - this.settingsButton.width - paddingH,
                paddingH
            );
        }
    }

    private positionTurnQueue() {
        const padding = 20;
        // console.log("Global Y:", this.turnQueueRenderer.getGlobalPosition().y);
        if (this.turnQueueRenderer) {
            this.turnQueueRenderer.position.set(
                // this.app.screen.width - this.turnQueueRenderer.width - padding,
                padding,
                // this.app.screen.height
                this.app.screen.height - padding
            );
        }
    }

    private positionSkillBar() {
        const paddingH = 20;
        const paddingV = 12;

        // const h = this.skillBarRenderer.getLayoutHeight();
        if (this.skillBarRenderer) {
            this.skillBarRenderer.position.set(
                // padding,
                this.app.screen.width - this.skillBarRenderer.width - paddingH,
                this.app.screen.height - this.skillBarRenderer.height - paddingV
            );
        }
    }

    private async handleGameStatus(status: BattleStatus) {
        if (status === BattleStatus.Playing || status === BattleStatus.Preparing) {
            if (!this.app.ticker.started) {
                // gsap.globalTimeline.resume();
                this.app.ticker.start();
            }
            return;
        }
        if (status === BattleStatus.Paused) {
            if (this.app.ticker.started) {
                // gsap.globalTimeline.pause();
                this.app.ticker.stop();
            }
            return;
        };
        if (status === BattleStatus.Victory || status === BattleStatus.Defeat) {
            this.showEndGameVisuals(status);
            return
        }
    }

    public async showEndGameVisuals(status: BattleStatus) {
        // if (status === BattleStatus.Playing || status === BattleStatus.Preparing) return;
        // SoundManager.stopBackground();
        this.controlStateMachine.setState(ControlState.Ended);
        this.unitLayer.eventMode = 'none';
        this.cameraManager.lock();
        this.tooltipManager.destroy();

        // const unitPositions = this.getUnitPositions();

        // await Promise.all([
        //     this.cameraManager.focusOnBattleCenter(unitPositions),
        // ]);
    
        // const isWin = status === BattleStatus.Victory;
        // const textStr = isWin ? "VICTORY" : "DEFEAT";
        // const mainColor = isWin ? 0xd6a84a : 0xcb0000;

        this.viewport.animate({
            scale: 0.25,
            time: 2000,
            ease: 'easeInOutExpo'
        });

        
        await new Promise(resolve => setTimeout(resolve, 1500));
        if (this.app.ticker.started) {
            // gsap.globalTimeline.pause();
            this.app.ticker.stop();
        }

        // const endGameStyle = new PIXI.TextStyle({
        //     fontFamily: 'Alegreya, serif',
        //     fontWeight: '800',
        //     fontSize: 100,
        //     letterSpacing: 8,
        //     fill: mainColor,
        //     stroke: { color: '#000000', width: 8 },
        //     dropShadow: { color: '#000000', blur: 5, distance: 4 }
        // });

        // this.endText = new PIXI.Text({ text: textStr, style: endGameStyle });
        // this.endText.anchor.set(0.5);
        // this.endText.x = this.app.screen.width / 2;
        // this.endText.y = this.app.screen.height / 2;
        // this.endText.alpha = 0;
        // this.endText.scale.set(5);

        // this.uiLayer.addChild(this.endText);
        

        // const tl = gsap.timeline();

        // tl.to(this.endText, {
        //     alpha: 1,
        //     scale: 1,
        //     duration: 1.2,
        //     ease: "elastic.out(1, 0.75)"
        // });
    }

    private async loadGameAssets() {
        await PIXI.Assets.load([
            // Юніти
            { src: resolveAsset('@assets/units/druzhynnyk.png') },
            { src: resolveAsset('@assets/units/varangian.png') },
            { src: resolveAsset('@assets/units/gryd.png') },
            { src: resolveAsset('@assets/units/striletz.png') },
            { src: resolveAsset('@assets/units/batyr.png') },
            { src: resolveAsset('@assets/units/kipchak.png') },
            { src: resolveAsset('@assets/units/bek.png') },
            { alias: 'footsteps', src: resolveAsset('@assets/footsteps.png') },
            { alias: 'unit_shadow', src: resolveAsset('@assets/units/unit_shadow.png') },

            // Інтерфейс
            { alias: 'portraits', src: resolveAsset('@assets/portraits.png') },
            { alias: 'badge', src: resolveAsset('@assets/badge.png') },
            { alias: 'badge_enemy', src: resolveAsset('@assets/badge_enemy.png') },
            { alias: 'skills', src: resolveAsset('@assets/skills/skills.png') },
            { alias: 'pips_active', src: resolveAsset('@assets/skills/pips_active.png') },
            { alias: 'pips_disabled', src: resolveAsset('@assets/skills/pips_disabled.png') },

            // Террейн
            { src: resolveAsset('@assets/terrain/hill_edge_center_front.png') },
            { src: resolveAsset('@assets/terrain/hill_edge_right_front.png') },
            { src: resolveAsset('@assets/terrain/hill_edge_right_rear.png') },
            { src: resolveAsset('@assets/terrain/hill_edge_center_rear.png') },
            { src: resolveAsset('@assets/terrain/hill_edge_left_rear.png') },
            { src: resolveAsset('@assets/terrain/hill_edge_left_front.png') },
            { src: resolveAsset('@assets/terrain/hill_top.png') },
            { src: resolveAsset('@assets/terrain/plains.png') },
            { src: resolveAsset('@assets/terrain/obstacles.png') },
            { src: resolveAsset('@assets/terrain/decals/grass.png') },
            { src: resolveAsset('@assets/terrain/decals/pebbles.png') },
            { src: resolveAsset('@assets/terrain/decals/decals.png') },

            // Шрифти
            { alias: 'KyivType', src: resolveAsset('@assets/fonts/KyivRegion-Regular.ttf') },
        ]);
    }

    /**
     * ПОВНЕ ОЧИЩЕННЯ СЦЕНИ
     */
    public _destroy(options?: PIXI.DestroyOptions) {
        console.warn("[BattleScene] Початок деструкції...");
        
        // Unsubscribe from status changes
        if (this.statusUnsubscribe) {
            this.statusUnsubscribe();
        }

        // Destroy managers
        if (this.battleManager) {
            this.battleManager.destroy();
        }
        
        if (this.inputController) {
            this.inputController.destroy();
        }
        
        if (this.tooltipManager) {
            this.tooltipManager.destroy();
        }

        // Clear ManagerRegistry
        ManagerRegistry.destroy();

        // Stop animations
        gsap.killTweensOf(this.viewport);
        gsap.killTweensOf(this.viewport.scale);
        if (this.endText) gsap.killTweensOf(this.endText);

        // Destroy viewport
        if (this.viewport) {
            this.viewport.destroy({ children: true });
        }

        // Destroy options
        const destroyOptions: PIXI.DestroyOptions = {
            children: true,
            texture: false,
        };

        if (typeof options === 'object' && options !== null) {
            Object.assign(destroyOptions, options);
        } else if (typeof options === 'boolean') {
            destroyOptions.children = options;
        }

        this.pathRenderer.destroy();
        this.damageText.destroy();

        super.destroy(destroyOptions);

        console.log("[BattleScene] Pixi об'єкти видалені.");
    }

    /**
     * ПОВНЕ ОЧИЩЕННЯ СЦЕНИ
     * Гарантує видалення всіх менеджерів, зупинку анімацій та відписку від подій.
     */
    public destroy(options?: PIXI.DestroyOptions | boolean) {
        console.warn("[BattleScene] Початок деструкції...");
        
        // 1. ВІДПИСКИ (найважливіше, щоб не було фонових викликів)
        if (this.statusUnsubscribe) {
            this.statusUnsubscribe();
            this.statusUnsubscribe = null;
        }

        BattleBus.off('FOCUS_ON_UNIT', this.handleFocusEvent);

        // Видаляємо слухачі подій PIXI/Viewport
        this.viewport.off('pointermove');
        this.viewport.off('pointerout');
        this.viewport.off('click');
        this.app.renderer.off('resize'); // Якщо був доданий через renderer
        if (this.app.ticker.started) {
            this.app.ticker.stop();
        }

        // 2. ЗУПИНКА МЕНЕДЖЕРІВ ТА ЛОГІКИ
        // BattleManager зазвичай керує станом гри, зупиняємо його першим
        this.battleManager?.destroy();
        this.inputController?.destroy();
        this.tooltipManager?.destroy();
        // this.aiTurnManager?.destroy(); // Важливо зупинити AI, щоб він не намагався робити хід
        // this.aiSystem?.destroy();
        // this.turnManager?.destroy();
        // this.skillManager?.destroy();
        // this.cameraManager?.destroy();
        
        // Очищуємо глобальний реєстр, щоб React-компоненти не посилалися на мертві об'єкти
        ManagerRegistry.destroy();

        // 3. ЗУПИНКА АНІМАЦІЙ
        // Зупиняємо всі GSAP анімації, пов'язані з елементами цієї сцени
        gsap.killTweensOf(this);
        gsap.killTweensOf(this.viewport);
        gsap.killTweensOf(this.viewport.scale);
        if (this.endText) {
            gsap.killTweensOf(this.endText);
        }
        // Зупиняємо AnimationController (якщо там є активні проміси або таймери)
        this.animationController?.cancelAll?.();

        // 4. ОЧИЩЕННЯ СПЕЦІАЛЬНИХ РЕНДЕРЕРІВ
        this.pathRenderer?.destroy();
        this.damageText?.destroy();
        this.turnQueueRenderer?.destroy();
        this.skillBarRenderer?.destroy();
        this.cursorManager?.destroy();

        // 5. ОЧИЩЕННЯ РЕЄСТРУ СУТНОСТЕЙ (entityRegistry)
        // Важливо, щоб посилання на UnitStack та BattleHex були видалені
        this.entityRegistry?.destroy?.();

        // 6. ВИДАЛЕННЯ ГРАФІЧНИХ ОБ'ЄКТІВ PIXI
        // Визначаємо налаштування видалення
        // Destroy options
        const destroyOptions: PIXI.DestroyOptions = {
            children: true,
            texture: false,
        };

        if (typeof options === 'object' && options !== null) {
            Object.assign(destroyOptions, options);
        } else if (typeof options === 'boolean') {
            destroyOptions.children = options;
        }

        // Видаляємо Viewport окремо (у нього свій метод destroy)
        if (this.viewport) {
            this.viewport.destroy({ children: true });
        }

        if (this.uiLayer) {
            this.uiLayer.destroy({ children: true });
        }

        // Викликаємо базовий метод PIXI.Container
        super.destroy(destroyOptions);

        console.log("[BattleScene] Деструкція завершена успішно.");
    }
}